import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DefaultAvatar } from "../../../../../components/common/DefaultAvatar";
import { formatPhoneForDisplay } from "../../../../../utils";

import { formatDateRu, getDaysLeft } from "../../../utils/date";
import {
    getStatusTone,
    pickCreatedAt,
    pickExpiresAt,
    pickListingsCount,
    pickPlanCode,
    safeString,
} from "../../../utils/user";
import {
    CreditCardIcon,
    IconBtn,
    KeyIcon,
    PencilIcon,
    RefreshIcon,
    StatusDot,
    TrashIcon,
} from "../UI";

type Props = {
    variant: "users" | "subscribers";
    users: any[];
    pending?: boolean;
    actionsDisabled?: boolean;
    expiringDays: number;
    onEditProfile?: (user: any) => void;
    onResetPassword?: (user: any) => void;
    onCreateSubscription?: (user: any) => void;
    onToggleListingAccess?: (user: any) => void;
    onDeleteUser?: (user: any) => void;
    listingAccessEmails?: Set<string>;
    listingAccessPending?: boolean;
    listingAccessActionEmail?: string | null;
};

const MobileView: React.FC<Props> = ({
    variant,
    users,
    pending,
    actionsDisabled = false,
    expiringDays,
    onEditProfile,
    onResetPassword,
    onCreateSubscription,
    onToggleListingAccess,
    onDeleteUser,
    listingAccessEmails,
    listingAccessPending,
    listingAccessActionEmail,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isUsersView = variant === "users";

    const openUser = (user: any) => {
        if (user?.id == null) return;
        sessionStorage.setItem("control-panel:return-scroll-y", String(window.scrollY));
        navigate(`/control-panel/users/${encodeURIComponent(String(user.id))}`, {
            state: { controlPanelReturn: true, controlPanelPath: `${location.pathname}${location.search}` },
        });
    };

    return (
        <div className="divide-y divide-slate-100 xl:hidden">
            {users.map((user: any, index: number) => {
                const name = safeString(user?.name) || "Без имени";
                const email = safeString(user?.email);
                const phone = formatPhoneForDisplay(user?.phone);
                const planCode = pickPlanCode(user);
                const listingsCount = pickListingsCount(user);
                const normalizedEmail = email.trim().toLowerCase();
                const hasListingAccess = Boolean(normalizedEmail && listingAccessEmails?.has(normalizedEmail));
                const listingAccessActionPending = listingAccessActionEmail === normalizedEmail;
                const expiresAt = pickExpiresAt(user);
                const createdAt = pickCreatedAt(user);
                const daysLeft = user?._ui?.daysLeft ?? getDaysLeft(expiresAt);
                const statusTone = getStatusTone(daysLeft, expiringDays);
                const dateLabel = isUsersView
                    ? (createdAt ? formatDateRu(createdAt) : "—")
                    : (expiresAt ? formatDateRu(expiresAt) : "—");

                return (
                    <article
                        key={String(user?.id ?? index)}
                        className="cursor-pointer px-4 py-5 transition-colors hover:bg-[#fff8fc] focus:bg-[#fff8fc] sm:px-5"
                        role="link"
                        tabIndex={user?.id == null ? -1 : 0}
                        onClick={() => openUser(user)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                openUser(user);
                            }
                        }}
                        aria-label={`Открыть профиль пользователя ${name}`}
                    >
                        <div className="flex items-start gap-3">
                            <DefaultAvatar name={name} color={user?.avatarColor} height={44} width={44} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <h3 className="m-0 break-words text-[15px] font-semibold leading-5 text-slate-900">{name}</h3>
                                        <p className="m-0 mt-0.5 text-xs text-slate-500">ID {user?.id ?? "—"}</p>
                                    </div>
                                    {!isUsersView ? <StatusDot tone={statusTone} /> : null}
                                </div>
                                <p className="m-0 mt-2 break-all text-sm text-slate-700">{email || "Email не указан"}</p>
                                <p className="m-0 mt-0.5 text-xs text-slate-500">{phone || "Телефон не указан"}</p>
                            </div>
                        </div>

                        <dl className="mt-4 grid grid-cols-3 gap-2 border-y border-slate-100 py-3">
                            <div>
                                <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Объявления</dt>
                                <dd className="m-0 mt-1 text-lg font-semibold leading-none tabular-nums text-slate-900">{listingsCount ?? "—"}</dd>
                            </div>
                            <div>
                                <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Тариф</dt>
                                <dd className="m-0 mt-1 truncate text-sm font-semibold text-slate-700">{planCode || "—"}</dd>
                            </div>
                            <div>
                                <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{isUsersView ? "Регистрация" : "Подписка до"}</dt>
                                <dd className="m-0 mt-1 text-sm font-semibold text-slate-700">{dateLabel}</dd>
                            </div>
                        </dl>

                        <div className="mt-3 flex items-center justify-between gap-3">
                            {isUsersView ? (
                                <span className={[
                                    "rounded-full border px-2.5 py-1 text-xs font-semibold",
                                    hasListingAccess
                                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                        : "border-slate-200 bg-slate-50 text-slate-500",
                                ].join(" ")}>
                                    Маркет: {hasListingAccess ? "разрешён" : "закрыт"}
                                </span>
                            ) : <span className="text-xs text-slate-500">Открыть профиль</span>}
                            <div
                                className="flex shrink-0 items-center gap-1.5"
                                onClick={(event) => event.stopPropagation()}
                                onKeyDown={(event) => event.stopPropagation()}
                            >
                                <IconBtn title="Редактировать профиль" onClick={onEditProfile ? () => onEditProfile(user) : undefined} disabled={actionsDisabled}><PencilIcon /></IconBtn>
                                <IconBtn title="Сбросить пароль" onClick={onResetPassword ? () => onResetPassword(user) : undefined} disabled={actionsDisabled}><RefreshIcon /></IconBtn>
                                <IconBtn title="Подписка" tone="accent" onClick={onCreateSubscription ? () => onCreateSubscription(user) : undefined} disabled={actionsDisabled}><CreditCardIcon /></IconBtn>
                                {isUsersView ? (
                                    <IconBtn
                                        title={hasListingAccess ? "Запретить создание объявлений" : "Разрешить создание объявлений"}
                                        tone={hasListingAccess ? "accent" : "default"}
                                        onClick={onToggleListingAccess ? () => onToggleListingAccess(user) : undefined}
                                        disabled={actionsDisabled || listingAccessPending || listingAccessActionPending || !normalizedEmail}
                                    ><KeyIcon /></IconBtn>
                                ) : null}
                                <IconBtn title="Удалить пользователя" tone="danger" onClick={onDeleteUser ? () => onDeleteUser(user) : undefined} disabled={actionsDisabled}><TrashIcon /></IconBtn>
                            </div>
                        </div>
                    </article>
                );
            })}
            {pending ? <div className="px-4 py-5 text-sm text-slate-500 sm:px-5">Обновляем список…</div> : null}
        </div>
    );
};

export default MobileView;
